import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Languages } from 'lucide-react';

// Google Ads languageConstants (RMF C.30 — segmentação por idioma).
export const GOOGLE_LANGUAGES: Array<{ id: string; key: string; label: string }> = [
  { id: '1014', key: 'pt', label: 'Português' },
  { id: '1000', key: 'en', label: 'Inglês' },
  { id: '1003', key: 'es', label: 'Espanhol' },
  { id: '1002', key: 'fr', label: 'Francês' },
  { id: '1001', key: 'de', label: 'Alemão' },
  { id: '1004', key: 'it', label: 'Italiano' },
  { id: '1030', key: 'pl', label: 'Polonês' },
  { id: '1009', key: 'da', label: 'Dinamarquês' },
  { id: '1042', key: 'fil', label: 'Filipino' },
  { id: '1017', key: 'zhCN', label: 'Chinês (simplificado)' },
  { id: '1018', key: 'zhTW', label: 'Chinês (tradicional)' },
  { id: '1005', key: 'ja', label: 'Japonês' },
];

export const DEFAULT_GOOGLE_LANGUAGES = ['1014'];

export const useGoogleLanguageLabel = () => {
  const { t } = useTranslation();
  return (id: string) => {
    const lang = GOOGLE_LANGUAGES.find(l => l.id === String(id));
    return lang ? t(`campaigns.rmf.lang.${lang.key}`, lang.label) : `#${id}`;
  };
};

interface Props {
  value: string[];
  onChange: (languages: string[]) => void;
}

const GoogleLanguagesInput: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const label = useGoogleLanguageLabel();
  const selected = (value || []).map(String);

  const toggle = (id: string) => {
    if (selected.includes(id)) {
      if (selected.length === 1) return; // ao menos um idioma
      onChange(selected.filter(l => l !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        <Languages className="inline w-4 h-4 mr-1" />
        {t('campaigns.rmf.languagesTitle', 'Idiomas do público')}
      </label>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        {t('campaigns.rmf.languagesDesc', 'Google Ads: os anúncios aparecem para quem usa o Google nestes idiomas. Escolha ao menos um.')}
      </p>
      {selected.length === 0 && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">
          {t('campaigns.rmf.languagesNone', 'Nenhum idioma definido: o Google mostra para todos os idiomas.')}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {GOOGLE_LANGUAGES.map(lang => {
          const isSel = selected.includes(lang.id);
          return (
            <button
              key={lang.id}
              type="button"
              aria-pressed={isSel}
              onClick={() => toggle(lang.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition-colors ${
                isSel
                  ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                  : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300'
              }`}
            >
              {isSel && <Check className="w-3.5 h-3.5" />}
              {label(lang.id)}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default GoogleLanguagesInput;
